import React, { useEffect, useState, useMemo } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { api, apiErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { User, Megaphone, Bell, CheckCheck, X } from "lucide-react";

const USER_TYPES = new Set(["payout", "social", "message", "account", "clip", "wallet", "clipper"]);
const CAMPAIGN_TYPES = new Set(["announcement", "campaign", "campaign_update", "campaign_milestone"]);

function categorize(n) {
  if (CAMPAIGN_TYPES.has(n.type)) return "campaign";
  return "user";
}

function TypeBadge({ type }) {
  const map = {
    announcement: "bg-[#c8ff00]/15 text-[#c8ff00]",
    payout: "bg-cyan-500/15 text-cyan-700 dark:text-cyan-300",
    social: "bg-amber-500/15 text-amber-700 dark:text-amber-300",
    message: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300",
    account: "bg-sky-500/15 text-sky-700 dark:text-sky-300",
    clip: "bg-[#c8ff00]/15 text-[#c8ff00]",
    campaign: "bg-[#c8ff00]/15 text-[#c8ff00]",
    wallet: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300",
  };
  const label = String(type || "message").replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());
  return (
    <span data-testid={`notification-type-${type}`} className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${map[type] || "bg-muted text-muted-foreground"}`}>
      {label}
    </span>
  );
}

export function NotificationsSheet({ open, onOpenChange, onCountsChange }) {
  const [items, setItems] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("user");

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get("/notifications");
      setItems(res.data?.items || []);
      onCountsChange?.(res.data?.unread_count || 0);
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (open) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const userItems = useMemo(() => items.filter((n) => categorize(n) === "user"), [items]);
  const campaignItems = useMemo(() => items.filter((n) => categorize(n) === "campaign"), [items]);
  const userUnread = userItems.filter((n) => !n.read).length;
  const campaignUnread = campaignItems.filter((n) => !n.read).length;
  const displayed = activeTab === "user" ? userItems : campaignItems;

  async function markRead(id) {
    try {
      await api.post(`/notifications/${id}/read`);
      await load();
    } catch { /* ignore */ }
  }

  async function hide(id) {
    try {
      await api.post(`/notifications/${id}/hide`);
      await load();
    } catch { /* ignore */ }
  }

  async function clearHistory() {
    try {
      await api.post("/notifications/clear_history");
      await load();
    } catch { /* ignore */ }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        data-testid="notifications-sheet"
        side="right"
        className="w-[460px] max-w-[92vw] border-[#1a1a1a] bg-[#0a0a0a] text-white p-0"
      >
        <div className="flex h-full flex-col">
          {/* Header */}
          <div className="flex items-center justify-between gap-8 border-b border-[#1a1a1a] px-5 py-4 pr-14">
            <SheetTitle data-testid="notifications-title" className="flex items-center gap-2 text-white">
              <Bell size={18} /> Notifications
            </SheetTitle>
            <Button
              data-testid="notifications-clear"
              variant="ghost"
              size="sm"
              className="h-8 rounded-full px-4 text-xs text-zinc-600 hover:text-white hover:bg-white/5"
              onClick={clearHistory}
            >
              Clear all
            </Button>
          </div>

          {/* Tabs */}
          <div className="grid grid-cols-2 border-b border-[#1a1a1a]">
            <button
              data-testid="notif-tab-user"
              className={`flex items-center justify-center gap-2 py-3 text-sm font-medium transition-colors ${activeTab === "user" ? "border-b-2 border-[#c8ff00] text-white" : "text-zinc-500 hover:text-white"}`}
              onClick={() => setActiveTab("user")}
            >
              <User size={14} /> User
              {userUnread > 0 && (
                <span className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-[#c8ff00] px-1 text-[10px] font-bold text-black">
                  {userUnread}
                </span>
              )}
            </button>
            <button
              data-testid="notif-tab-campaign"
              className={`flex items-center justify-center gap-2 py-3 text-sm font-medium transition-colors ${activeTab === "campaign" ? "border-b-2 border-[#c8ff00] text-white" : "text-zinc-500 hover:text-white"}`}
              onClick={() => setActiveTab("campaign")}
            >
              <Megaphone size={14} /> Campaign
              {campaignUnread > 0 && (
                <span className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-[#c8ff00] px-1 text-[10px] font-bold text-black">
                  {campaignUnread}
                </span>
              )}
            </button>
          </div>

          {error && (
            <div className="mx-5 mt-4 rounded-xl border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-300">{error}</div>
          )}

          {/* Items */}
          <div data-testid="notifications-list" className="flex-1 overflow-y-auto space-y-2 p-4">
            {loading && <div className="text-sm text-zinc-500 py-4 text-center">Loading...</div>}

            {!loading && displayed.map((n) => (
              <div
                key={n.id}
                data-testid={`notification-${n.id}`}
                className={`rounded-xl border px-4 py-3 ${
                  n.read
                    ? "border-[#1a1a1a] bg-black"
                    : "border-[#c8ff00]/20 bg-[#c8ff00]/[0.03]"
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate text-sm font-semibold text-white">{n.title}</span>
                      <TypeBadge type={n.type} />
                    </div>
                    <div className="mt-0.5 text-xs text-zinc-500">{n.message}</div>
                    <div className="mt-1.5 text-[10px] text-zinc-700">{new Date(n.created_at).toLocaleString()}</div>
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    {!n.read && (
                      <button
                        data-testid={`notification-mark-read-${n.id}`}
                        className="rounded-full p-1 text-zinc-600 hover:bg-white/5 hover:text-white transition-colors"
                        onClick={() => markRead(n.id)}
                        title="Mark read"
                      >
                        <CheckCheck size={14} />
                      </button>
                    )}
                    <button
                      data-testid={`notification-hide-${n.id}`}
                      className="rounded-full p-1 text-zinc-600 hover:bg-rose-500/10 hover:text-rose-400 transition-colors"
                      onClick={() => hide(n.id)}
                      title="Dismiss"
                    >
                      <X size={14} />
                    </button>
                  </div>
                </div>
              </div>
            ))}

            {!loading && !displayed.length && (
              <div data-testid="notifications-empty-state" className="rounded-xl border border-dashed border-[#1a1a1a] px-4 py-10 text-center text-sm text-zinc-600">
                No {activeTab === "user" ? "user" : "campaign"} notifications
              </div>
            )}
          </div>

          <div className="border-t border-[#1a1a1a] p-4">
            <Button
              data-testid="notifications-refresh"
              className="w-full rounded-full bg-[#1a1a1a] text-white hover:bg-[#222]"
              onClick={load}
            >
              Refresh
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
